import type {
  Airline,
  Airport,
  AssignmentPreview,
  Attribution,
  BookingReferenceRequest,
  ChooseLegRequest,
  ClaimView,
  ContactRequest,
  ContactResult,
  CreateEnquiryRequest,
  DataDisputeRequest,
  Document,
  EligibilityBasisRequest,
  Enquiry,
  EventsRequest,
  ExchangeResponse,
  ExtraRequest,
  FlightLookupQuery,
  FlightLookupResult,
  FlightSearchQuery,
  FlightSearchResult,
  HandoffRequest,
  ItemDecisionRequest,
  ItemKey,
  LegalAnswerRequest,
  NextStep,
  NextStepQuery,
  PartLaterRequest,
  PartLaterResult,
  PassengerInput,
  PassengerLinkResult,
  PassengerPart,
  PassengersRequest,
  Referral,
  ReferralRequest,
  RenewResponse,
  ResultResponseRequest,
  Resume,
  ScanStarted,
  SelfReportRequest,
  SendLinkRequest,
  SignatureRequest,
  TripInput,
  Visit,
  WhatsappRequest,
} from './contract';

/**
 * Base path of the Intake API, same origin as the funnel (Caddy routes it). The contract's
 * `BASE_PATH`, written out: importing it would pull the contract's `endpoints` (and every
 * schema) into the production bundle. api.test.ts holds the two equal.
 */
export const API_BASE = '/claim/api/v1';

export interface SearchParams {
  q: string;
  /** ≤ 20; the API clamps. */
  limit?: number;
  signal?: AbortSignal;
}

/** Every read can be cancelled (TanStack Query passes its signal). */
export interface ReadOptions {
  signal?: AbortSignal;
}

export interface UploadOptions {
  /** 0…1 as the bytes go up (multipart uploads only). */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

export interface ScanInput {
  file: File;
  prefillContactFrom?: string;
  attribution?: Attribution;
}

export interface DocumentInput {
  file: File;
  purpose: 'booking_proof' | 'id' | 'extra' | 'data_dispute';
  /** `id`, `id:<n>` (a minor) or `extra`; absent for `booking_proof` and `data_dispute`. */
  itemKey?: 'id' | 'extra' | `id:${number}`;
}

/**
 * Everything the funnel asks the Intake API: one method per operation of
 * `@delaybuddy/contract` (`endpoints`, 41 operations), named by `operationId`, with path
 * parameters first, then the body or query. Mutations answer with the resource after the
 * change (usually the whole `Enquiry`), so the caller can run the resolver on fresh state.
 *
 * Failures are `ApiError` (the error envelope; `isExpired` for `410 link_expired`) or
 * `NetworkError`. Implementations: `createHttpApi()` (the real thing) and `createFakeApi()`
 * (in-memory, the default in dev).
 */
export interface IntakeApi {
  /* Reference data */
  searchAirports(params: SearchParams): Promise<Airport[]>;
  searchAirlines(params: SearchParams): Promise<Airline[]>;

  /* Enquiry lifecycle */
  createEnquiry(body: CreateEnquiryRequest): Promise<Enquiry>;
  createEnquiryFromScan(input: ScanInput, opts?: UploadOptions): Promise<ScanStarted>;
  getEnquiry(enquiryId: string, opts?: ReadOptions): Promise<Enquiry>;
  getNextStep(enquiryId: string, query?: NextStepQuery, opts?: ReadOptions): Promise<NextStep>;
  getResume(enquiryId: string, opts?: ReadOptions): Promise<Resume>;

  /* Flights and answers (S1–S7) */
  updateTrip(enquiryId: string, body: TripInput): Promise<Enquiry>;
  searchLegFlights(
    enquiryId: string,
    leg: number,
    query?: FlightSearchQuery,
    opts?: ReadOptions,
  ): Promise<FlightSearchResult>;
  lookupLegFlight(
    enquiryId: string,
    leg: number,
    query: FlightLookupQuery,
    opts?: ReadOptions,
  ): Promise<FlightLookupResult>;
  chooseLeg(enquiryId: string, leg: number, body: ChooseLegRequest): Promise<Enquiry>;
  setResultResponse(enquiryId: string, body: ResultResponseRequest): Promise<Enquiry>;
  setSelfReport(enquiryId: string, body: SelfReportRequest): Promise<Enquiry>;
  setLegalAnswer(enquiryId: string, body: LegalAnswerRequest): Promise<Enquiry>;
  setContact(enquiryId: string, body: ContactRequest): Promise<ContactResult>;
  /* S8 (contract 1.2, w5-06): claim the leg alone; "Think our data is wrong?" */
  setEligibilityBasis(enquiryId: string, body: EligibilityBasisRequest): Promise<Enquiry>;
  submitDataDispute(enquiryId: string, body: DataDisputeRequest): Promise<Enquiry>;

  /* Passengers and booking reference (S9, S10, S12) */
  setPassengers(enquiryId: string, body: PassengersRequest): Promise<Enquiry>;
  addPassenger(enquiryId: string, body: PassengerInput): Promise<Enquiry>;
  setBookingReference(enquiryId: string, body: BookingReferenceRequest): Promise<Enquiry>;

  /* Signatures and the handoff (S11, S11-sign-for, S11-handoff) */
  getAssignmentPreview(
    enquiryId: string,
    query?: { passenger?: number; timeZone?: string },
    opts?: ReadOptions,
  ): Promise<AssignmentPreview>;
  getAssignmentPdf(
    enquiryId: string,
    query?: { passenger?: number; timeZone?: string },
    opts?: ReadOptions,
  ): Promise<Blob>;
  signClaim(enquiryId: string, body: SignatureRequest): Promise<Enquiry>;
  signForMinor(enquiryId: string, n: number, body: SignatureRequest): Promise<Enquiry>;
  setHandoff(enquiryId: string, n: number, body: HandoffRequest): Promise<Enquiry>;
  sendPassengerLink(enquiryId: string, n: number, body: SendLinkRequest): Promise<PassengerLinkResult>;

  /* Items and documents (S10, S13) */
  decideItem(enquiryId: string, itemKey: ItemKey, body: ItemDecisionRequest): Promise<Enquiry>;
  setExtra(enquiryId: string, body: ExtraRequest): Promise<Enquiry>;
  uploadDocument(enquiryId: string, input: DocumentInput, opts?: UploadOptions): Promise<Document>;
  getDocument(enquiryId: string, documentId: string, opts?: ReadOptions): Promise<Document>;

  /* Views and small settings (S12) */
  getClaim(enquiryId: string, query?: { visit?: Visit }, opts?: ReadOptions): Promise<ClaimView>;
  setWhatsapp(enquiryId: string, body: WhatsappRequest): Promise<Enquiry>;
  postEvents(enquiryId: string, body: EventsRequest): Promise<void>;

  /* A fellow passenger's part (S15) */
  getPassengerPart(enquiryId: string, n: number, opts?: ReadOptions): Promise<PassengerPart>;
  getPartAssignmentPreview(
    enquiryId: string,
    n: number,
    opts?: ReadOptions,
    query?: { timeZone?: string },
  ): Promise<AssignmentPreview>;
  getPartAssignmentPdf(enquiryId: string, n: number, opts?: ReadOptions): Promise<Blob>;
  signPart(enquiryId: string, n: number, body: SignatureRequest): Promise<PassengerPart>;
  uploadPartDocument(
    enquiryId: string,
    n: number,
    input: { file: File },
    opts?: UploadOptions,
  ): Promise<Document>;
  getPartDocument(enquiryId: string, n: number, documentId: string, opts?: ReadOptions): Promise<Document>;
  deferPartId(enquiryId: string, n: number, body: PartLaterRequest): Promise<PartLaterResult>;

  /* Magic links (S14) */
  exchangeLink(token: string): Promise<ExchangeResponse>;
  renewLink(token: string): Promise<RenewResponse>;

  /* A Buddy link opened on landing (contract 1.2) */
  resolveReferral(body: ReferralRequest): Promise<Referral>;
}

/** The operation ids, for tests that check both implementations cover the contract. */
export type OperationId = keyof IntakeApi;
